import React, { useState, useEffect } from 'react';
import apiClient from '../utils/apiClient';
import Select from 'react-select';
import { SERVER_BASE } from '../config/server';
import { useGlobalFilter, useTable, usePagination, useSortBy } from "react-table";
import Swal from 'sweetalert2';
import ExcelJS from "exceljs";
import { saveAs } from "file-saver";
import DateRangePickerComponent from './CustomDateRangePicker.jsx';
import moment from 'moment';
import 'bootstrap/dist/css/bootstrap.min.css';

const GlobalFilter = ({ globalFilter, setGlobalFilter }) => (
  <div className="mb-2 d-flex justify-content-end">
    <label className="me-2 fw-bold mb-0 align-self-center">Search:</label>
    <input
      className="form-control form-control-sm w-25"
      value={globalFilter || ""}
      onChange={(e) => setGlobalFilter(e.target.value)}
      placeholder="Search all columns..."
    />
  </div>
);

const InventoryTable = ({ columns, data }) => {
  const {
    getTableProps,
    getTableBodyProps,
    headerGroups,
    prepareRow,
    page,
    nextPage,
    previousPage,
    canNextPage,
    canPreviousPage,
    pageOptions,
    gotoPage,
    setPageSize,
    state,
    setGlobalFilter,
  } = useTable(
    {
      columns,
      data,
      initialState: { pageIndex: 0, pageSize: 10 },
    },
    useGlobalFilter,
    useSortBy,
    usePagination
  );

  const { pageIndex, pageSize, globalFilter } = state;

  return (
    <div className="">
      <GlobalFilter globalFilter={globalFilter} setGlobalFilter={setGlobalFilter} />

      <table className="table table-bordered table-striped table-hover" {...getTableProps()}>
        <thead className="">
          {headerGroups.map((headerGroup) => (
            <tr {...headerGroup.getHeaderGroupProps()}>
              {headerGroup.headers.map((column) => (
                <th scope="col" {...column.getHeaderProps(column.getSortByToggleProps())}>
                  {column.render("Header")}
                  <span>
                    {column.isSorted ? (column.isSortedDesc ? " 🔽" : " 🔼") : ""}
                  </span>
                </th>
              ))}
            </tr>
          ))}
        </thead>

        <tbody {...getTableBodyProps()}>
          {page.map((row) => {
            prepareRow(row);
            return (
              <tr {...row.getRowProps()}>
                {row.cells.map((cell) => (
                  <td {...cell.getCellProps()}>{cell.render("Cell")}</td>
                ))}
              </tr>
            );
          })}
        </tbody>
      </table>

      {/* Pagination Controls */}
      <div className="d-flex justify-content-between align-items-center mt-3">
        <div>
          <button
            className="btn btn-sm btn-secondary me-2"
            onClick={() => previousPage()}
            disabled={!canPreviousPage}
          >
            Previous
          </button>
          <button
            className="btn btn-sm btn-secondary"
            onClick={() => nextPage()}
            disabled={!canNextPage}
          >
            Next
          </button>
        </div>

        <span>
          Page{" "}
          <strong>
            {pageIndex + 1} of {pageOptions.length}
          </strong>
        </span>

        <select
          className="form-select form-select-sm w-auto"
          value={pageSize}
          onChange={(e) => setPageSize(Number(e.target.value))}
        >
          {[10, 20, 50, 100].map((size) => (
            <option key={size} value={size}>
              Show {size}
            </option>
          ))}
        </select>
      </div>
    </div>
  );
};

const DepartmentInventory = ({ accessOfDepartments }) => {
  const [departments, setDepartments] = useState([]);
  const [selectedDept, setSelectedDept] = useState(null);
  const [reportData, setReportData] = useState({});
  const [loading, setLoading] = useState(false);
  const [dateRange, setDateRange] = useState([null, null]);
  const [activeTab, setActiveTab] = useState('');
  const code = localStorage.getItem('code');
  const token = localStorage.getItem('token');
  useEffect(() => {
    if (accessOfDepartments) {
      const deptArray = accessOfDepartments
        .split(',')
        .map((dept) => ({ value: dept.trim(), label: dept.trim() }));
      setDepartments(deptArray);
    }
  }, [accessOfDepartments]);

  const getReport = async () => {
    if (!selectedDept) {
      Swal.fire({
        toast: true,
        position: 'top-end',
        icon: 'warning',
        title: 'Please select department',
        showConfirmButton: false,
        timer: 3000,
        timerProgressBar: true,
      });
      return;
    }

    if (!dateRange[0] || !dateRange[1]) {
      Swal.fire({
        toast: true,
        position: 'top-end',
        icon: 'warning',
        title: 'Please select a valid date range',
        showConfirmButton: false,
        timer: 3000,
        timerProgressBar: true,
      });
      return;
    }

    setLoading(true);

    try {
      const [startDate, endDate] = dateRange;

      const res = await apiClient.get(`${SERVER_BASE}/getInventoryStockReport`, {
        params: {
          department: selectedDept.value,
          startDate,
          endDate,
        },
      });
      
      const data = res.data.reportByLocation || {};
      setReportData(data);

      const firstLocation = Object.keys(data)[0];
      setActiveTab('All Locations' || firstLocation);
    } catch (error) {
      console.error('Error fetching report:', error);
    } finally {
      setLoading(false);
    }
  };

  // 🔁 Combine all items from all locations into one list
  const allLocationsData = React.useMemo(() => {
    const merged = {};

    Object.values(reportData).forEach(locationItems => {
      locationItems.forEach(item => {
        const key = item.printName;

        if (!merged[key]) {
          merged[key] = { ...item };
        } else {
          merged[key].openingStock += item.openingStock;
          merged[key].addition += item.addition;
          merged[key].deduction += item.deduction;
          merged[key].closingStock += item.closingStock;
          // UOM assumed to be same
        }
      });
    });

    return Object.values(merged);
  }, [reportData]);
  const sanitizeSheetName = (name) => name.replace(/[*?:\\/[\]]/g, "-");

  const exportToExcel = async (locationData, totalData) => {
    const workbook = new ExcelJS.Workbook();
  
    const addSheetWithData = (sheetName, data) => {
      const sheet = workbook.addWorksheet(sanitizeSheetName(sheetName));
  
      if (!data || data.length === 0) return;
  
      // Get all headers from first row
      const columns = Object.keys(data[0]).map((key) => ({
        header: key,
        key,
        width: Math.max(key.length + 2, 15),
        style: { font: { bold: true } },
      }));
  
      sheet.columns = columns;
  
      // Add data rows
      data.forEach((row) => {
        sheet.addRow(row);
      });
  
      // Style headers
      sheet.getRow(1).eachCell((cell) => {
        cell.font = { bold: true };
        cell.fill = {
          type: "pattern",
          pattern: "solid",
          fgColor: { argb: "FFD9D9D9" },
        };
        cell.border = {
          top: { style: "thin" },
          left: { style: "thin" },
          bottom: { style: "thin" },
          right: { style: "thin" },
        };
        cell.alignment = { vertical: "middle", horizontal: "center" };
      });
    };
  
    // Add each location sheet
    Object.entries(locationData).forEach(([location, rows]) => {
      addSheetWithData(location, rows);
    });
  
    // Add merged total sheet
    addSheetWithData("All Locations Total", totalData);
  
    // Generate and save the Excel file
    const buffer = await workbook.xlsx.writeBuffer();
    const blob = new Blob([buffer], {
      type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
    });
  
    saveAs(blob, "InventoryReport.xlsx");
  };
  
  const getColumns = (data) => {
    if (!data || data.length === 0) return [];
  
    // Generate dynamic columns
    const dynamicCols = Object.keys(data[0]).map((key) => ({
      Header: key.replace(/([A-Z])/g, ' $1').replace(/^./, str => str.toUpperCase()),
      accessor: key,
    }));
  
    // Prepend S.No column
    return [
      {
        Header: "S.No",
        Cell: ({ row }) => row.index + 1,
      },
      ...dynamicCols,
    ];
  };
  
  
  return (
    <div className="formcontainer mt-3">
      <h5>Overall Department Inventory</h5>
      <div className="mb-4">
        <div className="row align-items-end mb-3">
          <div className="col-md-4">
            <label className="form-label">Department</label>
            <Select options={departments} value={selectedDept} onChange={setSelectedDept} />
          </div>
          <div className="col-md-4">
            <label className="form-label">Date Range</label>
            <DateRangePickerComponent value={dateRange} onChange={setDateRange} />
          </div>
          <div className="col-md-2">
            <button
              className="btn btn-outline-success w-100"
              onClick={getReport}
              disabled={loading}
            >
              {loading ? 'Loading...' : 'Get Report'}
            </button>
          </div>
          <div className="col-md-2">
          <button
            className="btn btn-outline-secondary w-100"
            onClick={() => exportToExcel(reportData, allLocationsData)}
          >
            Export to Excel
          </button>
          </div>
        </div>
      </div>

      {Object.keys(reportData).length > 0 && (
        <>
          <ul className="nav nav-tabs mb-3">
            <li className="nav-item">
              <button
                className={`nav-link ${activeTab === 'All Locations' ? 'active' : ''}`}
                onClick={() => setActiveTab('All Locations')}
              >
                Total
              </button>
            </li>
            {Object.keys(reportData).map(location => (
              <li className="nav-item" key={location}>
                <button
                  className={`nav-link ${activeTab === location ? 'active' : ''}`}
                  onClick={() => setActiveTab(location)}
                >
                  {location}
                </button>
              </li>
            ))}
          </ul>

          <div>
            {activeTab === 'All Locations' ? (
              <InventoryTable
              data={allLocationsData}
              columns={getColumns(allLocationsData)}
            />            
            ) : (
              reportData[activeTab] && <InventoryTable
              data={reportData[activeTab]}
              columns={getColumns(reportData[activeTab])}
            />            
            )}
          </div>
        </>
      )}
    </div>
  );
};

export default DepartmentInventory;
